async function apiDoc(path, { method = 'GET', body, signal } = {}) {
  const token = localStorage.getItem('authToken');
  const headers = {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
  };
  const res = await fetch(`/api/doc${path}`, {
    method,
    headers,
    signal,
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json.success === false) {
    const msg = json?.erro?.mensagem || json.message || `Erro ${res.status}`;
    const err = new Error(msg);
    err.status = res.status;
    err.codigo = json?.erro?.codigo;
    throw err;
  }
  return json.data;
}

export function fetchHierarchy(signal) {
  return apiDoc('/hierarquia', { signal });
}

export function searchDocs(q, limite = 30) {
  const qs = new URLSearchParams();
  if (q) qs.set('q', q);
  if (limite) qs.set('limite', String(limite));
  return apiDoc(`/busca?${qs.toString()}`);
}

export function createModule(nome) {
  return apiDoc('/modulos', { method: 'POST', body: { nome } });
}

export function renameModule(id, nome) {
  return apiDoc(`/modulos/${id}`, { method: 'PATCH', body: { nome } });
}

export function moduleImpact(id) {
  return apiDoc(`/modulos/${id}/impacto`);
}

export function deleteModule(id) {
  return apiDoc(`/modulos/${id}`, { method: 'DELETE' });
}

export function createTopic(idModulo, nome) {
  return apiDoc('/topicos', { method: 'POST', body: { idModulo, nome } });
}

export function renameTopic(id, nome) {
  return apiDoc(`/topicos/${id}`, { method: 'PATCH', body: { nome } });
}

export function topicImpact(id) {
  return apiDoc(`/topicos/${id}/impacto`);
}

export function deleteTopic(id) {
  return apiDoc(`/topicos/${id}`, { method: 'DELETE' });
}

export function moveTopic(id, idModuloDestino) {
  return apiDoc(`/topicos/${id}/mover`, { method: 'PATCH', body: { idModuloDestino } });
}

export function createDocumentation(idTopico, titulo) {
  return apiDoc('/documentacoes', { method: 'POST', body: { idTopico, titulo } });
}

export function getDocumentation(id) {
  return apiDoc(`/documentacoes/${id}`);
}

export function renameDocumentation(id, titulo) {
  return apiDoc(`/documentacoes/${id}`, { method: 'PATCH', body: { titulo } });
}

export function saveDocumentationContent(id, html) {
  return apiDoc(`/documentacoes/${id}/conteudo`, { method: 'PUT', body: { html } });
}

export function deleteDocumentation(id) {
  return apiDoc(`/documentacoes/${id}`, { method: 'DELETE' });
}

export function moveDocumentation(id, idTopicoDestino) {
  return apiDoc(`/documentacoes/${id}/mover`, { method: 'PATCH', body: { idTopicoDestino } });
}

export function reorderModules(ids) {
  return apiDoc('/modulos/reordenar', { method: 'PATCH', body: { ids } });
}

export function reorderTopics(idModulo, ids) {
  return apiDoc('/topicos/reordenar', { method: 'PATCH', body: { idModulo, ids } });
}

export function reorderDocumentations(idTopico, ids) {
  return apiDoc('/documentacoes/reordenar', { method: 'PATCH', body: { idTopico, ids } });
}

export function presignDocumentationImage(docId, { mimeType, sizeBytes, fileName }) {
  return apiDoc(`/documentacoes/${docId}/imagens/presign`, {
    method: 'POST',
    body: { mimeType, sizeBytes, fileName },
  });
}

export function completeDocumentationImage(docId, body) {
  return apiDoc(`/documentacoes/${docId}/imagens/complete`, {
    method: 'POST',
    body,
  });
}

export function deleteDocumentationImage(docId, guid) {
  return apiDoc(`/documentacoes/${docId}/imagens/${encodeURIComponent(guid)}`, {
    method: 'DELETE',
  });
}

/** Presign → PUT S3 → complete; retorna { guid, viewUrl }. */
export async function uploadDocumentationImage(docId, file) {
  if (!file) throw new Error('Arquivo não selecionado.');
  const mimeType = file.type || 'image/jpeg';
  const sizeBytes = file.size;
  const fileName = file.name || 'image.jpg';

  const presign = await presignDocumentationImage(docId, { mimeType, sizeBytes, fileName });
  const put = await fetch(presign.uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': mimeType },
    body: file,
  });
  if (!put.ok) {
    throw new Error('Falha ao enviar a imagem para o armazenamento.');
  }

  return completeDocumentationImage(docId, {
    key: presign.key,
    guid: presign.guid,
    fileName: presign.fileName || fileName,
    contentType: presign.contentType || mimeType,
    displayWidthPct: 100,
  });
}

/** Exporta backup completo do Manual do tenant da sessão. */
export function exportDocumentationBackup() {
  return apiDoc('/backup/export');
}

/** Restaura backup no tenant da sessão (arquivo portável entre clientes). */
export function importDocumentationBackup(backup) {
  return apiDoc('/backup/import', { method: 'POST', body: { backup } });
}

/** Converte resposta da hierarquia no shape do mock (modules/topics/docs). */
export function mapHierarchyToModules(data) {
  const mods = data?.modulos || [];
  return mods.map((m) => ({
    id: m.id,
    name: m.nome || m.name,
    topics: (m.topicos || []).map((t) => ({
      id: t.id,
      name: t.nome || t.name,
      docs: (t.documentacoes || []).map((d) => ({
        id: d.id,
        title: d.titulo || d.title,
        updated: d.updatedLabel || '',
        html: null,
        excerpt: d.excerpt || '',
        status: d.status,
      })),
    })),
  }));
}
